import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { Box, VStack, Heading, Text, Flex, Icon, useTheme } from "@chakra-ui/react";
import { IoHourglass, IoMailOpen } from "react-icons/io5";
import { useRouteMatch, useHistory } from "react-router-dom";

import { IframeOrFullPageWrapper } from "components/Container";
import Lottie from "components/Lottie";
import { FLOW_STATE, useAnalytics } from "utils";
import CheckmarkAnimation from "assets/animations/checkmark.json";

const TIMEOUT_DURATION = 3000;
const KYC_STATE_TO_FLOW_STATE_MAP = {
  ACCEPT: FLOW_STATE.COMPLETE,
  BASIC_PII_COLLECTED: FLOW_STATE.COMPLETE,
  MANUAL_REVIEW: FLOW_STATE.PENDING,
  DENY: FLOW_STATE.DENIED,
  TAX_UPDATE_COLLECTED: FLOW_STATE.COMPLETE,
};

/**
 * The success page for KYC. Checks the status of the application and relays information to
 * end-user based on that. When the application state becomes "ACCEPT" this component also
 * runs a Lottie animation on mount and gives the user the ability to "Continue" by either
 * going forward in the flow progress or returning to the publisher.
 *
 * @name Success
 * @author magnus
 * @returns JSX.Element
 */
export default function Success({
  applicationState,
  onComplete,
  successTimeoutDuration = TIMEOUT_DURATION, // only passing in as prop to allow for shortening in tests
}) {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const analytics = useAnalytics();
  const history = useHistory();
  const { path } = useRouteMatch();
  const viewEventFired = useRef(false);
  const onCompleteFired = useRef(false);

  useEffect(() => {
    /**
     * TODO: this is to get around a routing issue if
     * we come from the KYC /documents route.  The route
     * stays when jumping to payoutv2 and we need to clean it up.
     * Make sure this works for other cases and doesn't cause any issues.
    */
    if (history.location.pathname.endsWith("/documents")) {
      history.replace(`${path}/`);
    }
  }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    /**
     * We're only going to call onComplete if the user's
     * application is the 'ACCEPT' state.  Otherwise, they
     * be stuck.
     * Soon we'll have an option to show the 'Close' button
     * in this state, and the user can click that to
     * fire onComplete with a 'pending' flow state.
     */
    let onSuccessTimeout = null;
    if (Object.keys(KYC_STATE_TO_FLOW_STATE_MAP).includes(applicationState) && !onSuccessTimeout) {
      const state = KYC_STATE_TO_FLOW_STATE_MAP[applicationState];
      onSuccessTimeout = setTimeout(() => {
        if (!onCompleteFired.current) {
          onComplete({
            state,
            trigger: `kyc-${applicationState}`.toLowerCase(),
            result: applicationState,
          });
          // since the onComplete callback that's passed through from FlowStepsHandler gets updated after
          // being called we don't want this to cause a loop in the useEffect that keeps firing onComplete in this case
          onCompleteFired.current = true;
        }
      }, successTimeoutDuration);
    }
    return () => {
      clearTimeout(onSuccessTimeout);
    };
  }, [applicationState, onComplete, successTimeoutDuration]);

  let title = "";
  let description = "";
  let icon = undefined;
  // let buttonText = "";

  switch (applicationState) {
    case "ACCEPT": {
      title = formatMessage({
        defaultMessage: "You’re all set!",
        description: "User status message",
      });
      description = formatMessage({
        defaultMessage: "Your identity has been verified and your Tilia wallet is ready to use.",
        description: "User status message",
      });
      // buttonText = "Continue";
      break;
    }

    case "TAX_UPDATE_COLLECTED":
    case "BASIC_PII_COLLECTED": {
      title = formatMessage({
        defaultMessage: "You’re all set!",
        description: "User status message",
      });
      description = "Your information has been submitted.";
      // buttonText = "Continue";
      break;
    }

    case "MANUAL_REVIEW": {
      title = formatMessage({
        defaultMessage: "Under review",
        description: "User status message",
      });
      description = formatMessage({
        defaultMessage:
          "Your identity verification will require human review. You will receive an email once we have an update. This usually takes 3 business days.",
        description: "User status message",
      });
      icon = IoHourglass;
      // buttonText = "Close";
      break;
    }

    case "INVALID_TOKEN":
    default: {
      title = formatMessage({
        defaultMessage: "We’ll be in touch",
        description: "User status message",
      });
      description = formatMessage({
        defaultMessage:
          "We are unable to verify the state of your identity verification right now. Please keep an eye out for an email from us with more information.",
        description: "User status message",
      });
      icon = IoMailOpen;
      // buttonText = "Close";
      break;
    }
  }

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("KYC", "KYC_Success_Viewed", { title, description, applicationState });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, applicationState, title, description]);

  // const handleDoneButton = () => {
  //   onComplete({
  //     state: applicationState === "ACCEPT" ? FLOW_STATE.COMPLETE : FLOW_STATE.CANCEL,
  //     trigger: applicationState === "ACCEPT" ? "continue-button" : "close-button",
  //     result: applicationState,
  //   });
  // };

  const animationColors = [
    theme.colors.integrator[500],
    theme.colors.white,
    theme.colors.integrator[500],
    theme.colors.white,
    theme.colors.integrator[500],
  ];

  return (
    <IframeOrFullPageWrapper>
      <VStack spacing={4} px={4} pb={6}>
        <Box>
          <Heading mb={2}>{title}</Heading>
          <Text fontSize="sm" color="secondaryText">
            {description}
          </Text>
        </Box>

        {icon ? (
          <Icon fontSize={140} as={icon} color="integrator.500" />
        ) : (
          <Flex alignItems="center" justifyContent="center">
            <Lottie
              animation={CheckmarkAnimation}
              width={300}
              height={300}
              colors={animationColors}
              loop={false}
              additionalDivProps={{
                "data-testid": "lottie-checkmark-animation",
              }}
            />
          </Flex>
        )}

        {/* Holding on to button in code as showing the button will become an option */}
        {/* <Button onClick={handleDoneButton} variant="outline" colorScheme="gray" width="full">
          {buttonText}
        </Button> */}
      </VStack>
    </IframeOrFullPageWrapper>
  );
}
